import { useFormatters } from '@/hooks/useFormatters';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import DeviceNameSpan from '@components/deviceNameSpan';
import type { ActiveGateMovement } from '@features/gateCommon/hooks/useActiveGateMovements';
import { formatPosition } from '@features/gateCommon/utils/formatPosition';
import { describeMovementStatus } from '@features/gateCommon/utils/gateMovementState';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Link, Typography } from '@mui/material';
import type { FC } from 'react';
import { BannerRow, type BannerRowPosition } from './BannerRow';
import { BorderLinearProgress } from './BorderLinearProgress';

/**
 * Fila de un movimiento v2 (AMARU-911). El rótulo sale de `describeMovementStatus`, la misma regla
 * del historial y del panel de operación: una solicitud en cola cancelada o vencida no se lee
 * «Finalizada».
 */
export const V2MovementRow: FC<BannerRowPosition & { movement: ActiveGateMovement }> = ({
  movement,
  ...position
}) => {
  const { i18n } = useLingui();
  const { formatMetricValue } = useFormatters();
  const { setModalState } = useGlobalModal('gateOperationModal');

  const { label } = describeMovementStatus(movement, i18n, Date.now());
  const progress = movement.operationProgress;
  // `startPosition` es null en cola y `requestedPosition` en una operación manual: se omite en vez
  // de mostrar 0.
  const start = movement.startPosition?.[1];
  const target = movement.requestedPosition?.[1];
  const gateName = <DeviceNameSpan deviceId={movement.gate.id} inlineError />;

  return (
    <BannerRow {...position}>
      <Typography noWrap>
        <Trans>
          La compuerta <b>{gateName}</b>: {label}
        </Trans>
      </Typography>
      <Box display="flex" alignItems="center" gap={1}>
        <Box width={100}>
          <BorderLinearProgress
            variant={progress == null ? 'indeterminate' : 'determinate'}
            value={progress ?? undefined}
          />
        </Box>
        {progress != null && (
          <Typography variant="body2" color="#222" noWrap>
            {Math.round(progress)}%
          </Typography>
        )}
      </Box>
      {(start != null || target != null) && (
        <Typography variant="body2" color="#222" noWrap>
          {start != null && (
            <>
              <Trans>Posición inicial</Trans>: {formatPosition(start, formatMetricValue)}
            </>
          )}
          {start != null && target != null && ' - '}
          {target != null && (
            <>
              <Trans>Posición objetivo</Trans>: {formatPosition(target, formatMetricValue)}
            </>
          )}
        </Typography>
      )}
      <Link
        component="button"
        color="inherit"
        sx={{ fontWeight: 'bold' }}
        noWrap
        onClick={() => setModalState({ open: true, movementId: movement.id })}
      >
        <Trans>Ver más</Trans>
      </Link>
    </BannerRow>
  );
};
